/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable react/function-component-definition */
import {
  Container,
  Grid,
  Skeleton,
  Typography,
  Card,
  CardContent,
  Box,
  Divider,
  Avatar,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import SchoolIcon from '@mui/icons-material/School';
import PersonIcon from '@mui/icons-material/Person';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LanguageIcon from '@mui/icons-material/Language';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import DeleteIcon from '@mui/icons-material/Delete';
import VerifiedIcon from '@mui/icons-material/Verified';
import PendingIcon from '@mui/icons-material/Pending';

import { School } from '../../../../types';
import { axios } from '../../../../clientProvider';
import StudentList from './StudentList';
import AdminPageWrapper from '../../../../layouts/AdminLayout/admin-page-wrapper';
import DeleteSchool from '../DeleteSchool';
import { CustomModal } from '../../../../components/Modal';

const getSchool = async (id: string | undefined): Promise<School> => {
  const { data: res } = await axios.get(`/School/view_school/${id}`);
  return res.school;
};

const SchoolView: React.FC<School> = () => {
  const { id } = useParams();
  const { data } = useQuery(['SchoolView', id], () => getSchool(id));
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const handleDeleteClick = () => {
    setDeleteModalOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteModalOpen(false);
  };

  const StatsCard = ({ title, value, icon, color = '#FF6B35' }) => (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        border: '1px solid #E0E0E0',
        borderRadius: '12px',
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
        }
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: color, width: 48, height: 48 }}>{icon}</Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h3" fontWeight={700} color="text.primary">
              {value || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {title}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const InfoCard = ({ title, children, icon }) => (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        border: '1px solid #E0E0E0',
        borderRadius: '12px'
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Avatar sx={{ bgcolor: '#FF6B35', width: 40, height: 40 }}>
            {icon}
          </Avatar>
          <Typography variant="h6" fontWeight={600} color="text.primary">
            {title}
          </Typography>
        </Box>
        {children}
      </CardContent>
    </Card>
  );

  const page = (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Header Section with School Name and Actions */}
      <Card
        elevation={0}
        sx={{
          mb: 3,
          border: '1px solid #E0E0E0',
          borderRadius: '12px',
          background: '#1B4332'
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Avatar
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  width: 64,
                  height: 64,
                  backdropFilter: 'blur(10px)'
                }}
              >
                <SchoolIcon sx={{ fontSize: 32, color: 'white' }} />
              </Avatar>
              <Box>
                <Typography
                  variant="h4"
                  fontWeight={700}
                  color="white"
                  sx={{ mb: 1 }}
                >
                  {data?.name || 'Loading...'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Chip
                    icon={data?.isVerified ? <VerifiedIcon /> : <PendingIcon />}
                    label={
                      data?.isVerified
                        ? 'Verified School'
                        : 'Pending Verification'
                    }
                    sx={{
                      bgcolor: data?.isVerified
                        ? 'rgba(76, 175, 80, 0.2)'
                        : 'rgba(255, 193, 7, 0.2)',
                      color: 'white',
                      fontWeight: 600,
                      '& .MuiChip-icon': { color: 'white' }
                    }}
                  />
                  <Chip
                    label={data?.levelOfEducation || 'N/A'}
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      fontWeight: 600,
                      backdropFilter: 'blur(10px)'
                    }}
                  />
                </Box>
              </Box>
            </Box>
            <Tooltip title="Delete School" arrow>
              <IconButton
                onClick={handleDeleteClick}
                sx={{
                  bgcolor: 'rgba(244, 67, 54, 0.2)',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(244, 67, 54, 0.3)',
                    transform: 'scale(1.05)'
                  },
                  backdropFilter: 'blur(10px)'
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Total Teachers"
            value={data?.teachers?.length}
            icon={<PersonIcon />}
            color="#2196F3"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StudentList />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Education Level"
            value={data?.levelOfEducation || 'N/A'}
            icon={<SchoolIcon />}
            color="#9C27B0"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Status"
            value={data?.isVerified ? 'Verified' : 'Pending'}
            icon={data?.isVerified ? <VerifiedIcon /> : <PendingIcon />}
            color={data?.isVerified ? '#4CAF50' : '#FF9800'}
          />
        </Grid>
      </Grid>

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* School Details */}
        <Grid item xs={12} lg={8}>
          <InfoCard title="School Information" icon={<SchoolIcon />}>
            {data ? (
              <Box>
                <Grid container spacing={2}>
                  {data.country && (
                    <Grid item xs={12} sm={6}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 2
                        }}
                      >
                        <LocationOnIcon color="action" fontSize="small" />
                        <Typography variant="body2" color="text.secondary">
                          Country
                        </Typography>
                      </Box>
                      <Typography variant="body1" fontWeight={600}>
                        {data.country}
                      </Typography>
                    </Grid>
                  )}
                  {data.city && (
                    <Grid item xs={12} sm={6}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 2
                        }}
                      >
                        <LocationOnIcon color="action" fontSize="small" />
                        <Typography variant="body2" color="text.secondary">
                          City
                        </Typography>
                      </Box>
                      <Typography variant="body1" fontWeight={600}>
                        {data.city}
                      </Typography>
                    </Grid>
                  )}
                  {data.motto && (
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        School Motto
                      </Typography>
                      <Typography
                        variant="body1"
                        fontStyle="italic"
                        color="text.primary"
                      >
                        {data.motto}
                      </Typography>
                    </Grid>
                  )}
                  {data.website && (
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 1
                        }}
                      >
                        <LanguageIcon color="action" fontSize="small" />
                        <Typography variant="body2" color="text.secondary">
                          Website
                        </Typography>
                      </Box>
                      <Typography
                        variant="body1"
                        color="primary"
                        component="a"
                        href={data.website}
                        target="_blank"
                        sx={{
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' }
                        }}
                      >
                        {data.website}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
            ) : (
              <Box>
                <Skeleton
                  variant="text"
                  width="60%"
                  height={30}
                  sx={{ mb: 2 }}
                />
                <Skeleton
                  variant="text"
                  width="40%"
                  height={30}
                  sx={{ mb: 2 }}
                />
                <Skeleton
                  variant="text"
                  width="80%"
                  height={30}
                  sx={{ mb: 2 }}
                />
                <Skeleton variant="text" width="50%" height={30} />
              </Box>
            )}
          </InfoCard>
        </Grid>

        {/* Contact Information */}
        <Grid item xs={12} lg={4}>
          <Grid container spacing={3}>
            {/* Point of Contact */}
            <Grid item xs={12}>
              <InfoCard title="Point of Contact" icon={<PersonIcon />}>
                {data?.admin ? (
                  <Box>
                    <Typography
                      variant="h6"
                      fontWeight={600}
                      color="text.primary"
                      sx={{ mb: 2 }}
                    >
                      {data.admin.firstName} {data.admin.lastName}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1
                      }}
                    >
                      <PhoneIcon color="action" fontSize="small" />
                      <Typography variant="body2" color="text.primary">
                        {data.admin.phone}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EmailIcon color="action" fontSize="small" />
                      <Typography variant="body2" color="text.primary">
                        {data.admin.email}
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    <Skeleton width="60%" height={25} sx={{ mb: 2 }} />
                    <Skeleton width="80%" height={20} sx={{ mb: 1 }} />
                    <Skeleton width="70%" height={20} />
                  </Box>
                )}
              </InfoCard>
            </Grid>

            {/* Principal (if different from contact) */}
            <Grid item xs={12}>
              <InfoCard title="Principal" icon={<PersonIcon />}>
                {data?.admin ? (
                  <Box>
                    <Typography
                      variant="h6"
                      fontWeight={600}
                      color="text.primary"
                      sx={{ mb: 2 }}
                    >
                      {data.admin.firstName} {data.admin.lastName}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1
                      }}
                    >
                      <PhoneIcon color="action" fontSize="small" />
                      <Typography variant="body2" color="text.primary">
                        {data.admin.phone}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EmailIcon color="action" fontSize="small" />
                      <Typography variant="body2" color="text.primary">
                        {data.admin.email}
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    <Skeleton width="60%" height={25} sx={{ mb: 2 }} />
                    <Skeleton width="80%" height={20} sx={{ mb: 1 }} />
                    <Skeleton width="70%" height={20} />
                  </Box>
                )}
              </InfoCard>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Delete Confirmation Modal */}
      <CustomModal
        open={deleteModalOpen}
        handleClose={handleDeleteClose}
        title="Delete School"
      >
        {deleteModalOpen && (
          <DeleteSchool
            selected={{ id: id! }}
            handleClose={handleDeleteClose}
          />
        )}
      </CustomModal>
    </Container>
  );

  return (
    <AdminPageWrapper
      pageTitle={data?.name || 'School Details'}
      showBackButton
      page={page}
    />
  );
};

export default SchoolView;
